import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Callout, FontIcon, IconButton, Text } from '@fluentui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Comment, GridRow, ResourceType, UserType, WorkType } from '../../../../../../../../../../../api/generated/data-contracts';
import { COMMENTS } from '../../../../../../../../../../../context/network/http/QueryProvider/queryKeys';
import { useMemoQueryKey } from '../../../../../../../../../../../context/network/http/QueryProvider/queries/useMemoQueryKey';
import { useUISettings } from '../../../../../../../../../../../context/network/http/QueryProvider/queries/UISettings';
import { useTPPermissions } from '../../../../../../../../../../../hooks/usePermissions';
import { PFSpinner } from '../../../../../../../../../../../Components/Common/Spinner';
import { CommentBox } from '../../../../../../../../../../../Components/Overview/Comments/components/CommentBox';
import { CommentTextField } from '../../../../../../../../../../../Components/Overview/Comments/components/CommentTextField';
import { ApiCalls } from '../../../../../../../../../../../api/api';
import { useGridRowContext } from '../../../../../../../../../_grid/contexts/rowContext';
import { useStore } from '../../../../../../../../../../../context/store';
import shallow from 'zustand/shallow';
import { isEqual } from 'date-fns';
import { useTranslation } from '../../../../../../../../../../../Locales/i18n/localizationService';

interface IProps {
    elementId: string;
}

type ResourceGridRow = GridRowWithMetadata<{
    Active?: boolean;
    HasComments?: boolean;
    InAccessible?: boolean;
    LastCommentUtc?: Date;
    ResourceType: ResourceType;
    WorkType: WorkType;
}>;

export const Comments = ({ elementId }: IProps) => {
	const { t } = useTranslation();
    const {
        row,
        rowRelations: [contractRow],
    } = useGridRowContext();
    const [calloutVisible, setCalloutVisible] = useState<boolean>(false);
    const canDelete = useTPPermissions({ userTypes: [UserType.SuperUser] });

    const [disabled, setDisabled] = useState<boolean>(false);
    const [hasComments, setHasComments] = useState<boolean>(() => Boolean(row.metadata.HasComments));

    const queryKey = useMemoQueryKey(COMMENTS, row.identity.id);

    const {
        data: comments,
        isPending,
        refetch: refetchComments,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            // const commentsFetched = await fetchCallback(row);
            const commentsFetched = await ApiCalls.getContractCommentsForResource({ contractId: contractRow.identity.id, resourceId: row.identity.id }).then(
                data => data.data,
            );
            setDisabled(commentsFetched == null);
            return commentsFetched ?? [];
        },
        staleTime: 1000 * 20,
        enabled: calloutVisible,
        refetchInterval: 1000 * 20,
    });
    const queryClient = useQueryClient();

    const removeComment = useCallback(
        async (comment: Comment) => {
            // await removeCallback(comment);
            await ApiCalls.deleteComment({ contractId: contractRow.identity.id, commentId: comment.id });
            shouldSmoothScroll.current = false;
            queryClient.setQueryData(queryKey, (comments: Comment[]) => comments.filter(existingCommment => existingCommment.id !== comment.id));
        },
        [contractRow.identity.id, queryClient, queryKey],
    );

    const { data: uiSettings } = useUISettings();
    const resource = uiSettings.resource;
    const addComment = useCallback(
        async (text: string) => {
            try {
                const timestamp = new Date();
                timestamp.setHours(0, 0, 1); // hack to prevent "unread" flag, due to UTC time coming from API
                const comment: Comment = {
                    text,
                    timestamp: timestamp as any,
                    ...(resource && {
                        authorId: resource.id,
                        authorName: resource.name,
                    }),
                };
                if (!hasComments) {
                    setHasComments(true);
                }
                await ApiCalls.upsertComment({
                    contractId: contractRow.identity.id,
                    contractName: contractRow.name,
                    resourceId: row.identity.id,
                    comment,
                }).then(data => data.data);
                await refetchComments();
            } catch (error) {
                console.log('addComment ~ error', error);
            }
        },
        [contractRow.identity.id, contractRow.name, hasComments, refetchComments, resource, row.identity.id],
    );

    const editComment = useCallback(
        async (text: string, comment: Comment) => {
            try {
                const newComment = await ApiCalls.upsertComment({
                    contractId: contractRow.identity.id,
                    contractName: contractRow.name,
                    resourceId: row.identity.id,
                    comment: { ...comment, text },
                }).then(data => data.data);
                queryClient.setQueryData<Comment[]>(queryKey, comments =>
                    comments.map(oldComment => {
                        if (oldComment.id === comment.id) {
                            return newComment;
                        }
                        return oldComment;
                    }),
                );
                shouldSmoothScroll.current = false;
            } catch (error) {
                console.log('addComment ~ error', error);
            }
        },
        [contractRow.identity.id, contractRow.name, queryClient, queryKey, row.identity.id],
    );
    const ref = useRef<HTMLDivElement | null>(null);
    const shouldSmoothScroll = useRef(true);

    useLayoutEffect(() => {
        if (!calloutVisible || isPending) return;

        const timeout = setTimeout(() => {
            if (ref.current) {
                ref.current.scrollTo({
                    top: ref.current.scrollHeight,
                    behavior: 'smooth',
                    // behavior: 'auto',
                });
            }
        }, 300);

        return () => {
            clearTimeout(timeout);
        };
    }, [calloutVisible, isPending]);

    useLayoutEffect(() => {
        if (!calloutVisible) return;
        if (shouldSmoothScroll.current) {
            const timeout = setTimeout(() => {
                if (ref.current) {
                    ref.current.scrollTo({
                        top: ref.current.scrollHeight,
                        behavior: 'smooth',
                        // behavior: 'auto',
                    });
                }
            }, 300);

            return () => {
                clearTimeout(timeout);
            };
        } else {
            shouldSmoothScroll.current = true;
        }
    }, [calloutVisible, comments]);

    const isSuperUser = useTPPermissions({ userTypes: [UserType.SuperUser] });

    const { confirmRead, hasUnread } = useAllocationRowCommmentsRead(row);

    return (
        <>
            <IconButton
                id={elementId}
                iconProps={{ iconName: 'Comment' }}
                className={`tp-comments-button ${hasUnread ? 'unread' : hasComments ? 'read' : ''}`}
                onClick={() => {
                    setCalloutVisible(c => !c);
                    if (hasUnread) {
                        confirmRead();
                    }
                }}
            />
            {calloutVisible && (
                <Callout
                    id="tp-comment-callout"
                    className="tp-comments-callout"
                    role="alertdialog"
                    gapSpace={0}
                    target={`#${elementId}`}
                    onDismiss={ev => setCalloutVisible(false)}
                    setInitialFocus={true}
                    hidden={!calloutVisible}
                >
                    <div className="tp-comments-container">
                        {isPending ? (
                            <PFSpinner />
                        ) : (
                            <>
                                <div className="ms-CalloutExample-header">
                                    <p className="ms-CalloutExample-title">{t("Comments.Comments")}</p>
                                </div>
                                {disabled ? (
                                    <div>
                                        <FontIcon iconName="Warning" />
                                        <Text styles={{ root: { paddingLeft: 10 } }}>{'Contract does not exist'}</Text>
                                    </div>
                                ) : (
                                    <>
                                        <div className="tp-comments-list" ref={ref}>
                                            {comments.map(comment => {
                                                return (
                                                    <CommentBox
                                                        key={comment.id}
                                                        comment={comment}
                                                        removeComment={removeComment}
                                                        editComment={editComment}
                                                        canDelete={canDelete}
                                                        isSuperUser={isSuperUser}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <CommentTextField addComment={addComment} />
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </Callout>
            )}
        </>
    );
};

type GridRowWithMetadata<T> = Omit<GridRow, 'metadata'> & { metadata: T };

const allocationViewKey = 'allocation';
const useAllocationRowCommmentsRead = (row: GridRow) => {
    const { lastRead, setComments } = useStore(store => {
        const lastRead = store.comments[allocationViewKey]?.[row.identity.id];
		if (lastRead) {
			lastRead.setMilliseconds(0)
		}
        return {
            lastRead,
            setComments: store.setComments,
        };
    }, shallow);

	const hasUnread = useMemo(() => {
        if ((row as ResourceGridRow).metadata.LastCommentUtc) {
			const date = new Date((row as ResourceGridRow).metadata.LastCommentUtc);
			date.setMilliseconds(0);
            return !isEqual(date, lastRead);
        }
        return false;
    }, [lastRead, row]);

    const confirmRead = useCallback(() => {
		const date = new Date((row as ResourceGridRow).metadata.LastCommentUtc);
		date.setMilliseconds(0)
        setComments(allocationViewKey, row.identity.id, date);
    }, [row, setComments]);

    return {
        hasUnread,
        confirmRead,
    };
};
